import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { literalTextToRegex } from '../../../util/string-convert';
import { StoreState } from '../../../store/store';
import { createNewAeTitleRule, generateNewId, createNewDicomAttributeRule, createNewDicomRule } from '../../../store/global-types/customization-helpers';
import { ModelType } from '../../../store/global-types/customization-types';
import { ModelSelectionUndoState } from '../ModelSelectionPage';
import SelectionRulesForm from '../SelectionRulesForm';
import { aeTitleRuleAdded, dicomAttributeRuleAdded, dicomRuleAdded, adaptSelectors } from '../../../store/adapt/adaptSlice';
import AdaptDicomAttributeRule from './AdaptDicomAttributeRule';
import AdaptAeTitleRule from './AdaptAeTitleRule';
import AdaptDicomRule from './AdaptDicomRule';

interface AdaptSelectionRulesProps {
    customizationBaseId: string,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    heading: string,
    hideCustomizationName?: boolean,
    showMultipleOutputsWarning: boolean,
    /** If true, don't show undo UI options. */
    isUndoDisabled?: boolean,
}

const AdaptSelectionRules = (props: AdaptSelectionRulesProps) => {
    const { customizationBaseId, setUndoState, heading, hideCustomizationName, showMultipleOutputsWarning, isUndoDisabled } = props;

    const dispatch = useDispatch();

    const customizationBase = useSelector((state: StoreState) => adaptSelectors.selectCustomizationBaseById(state, customizationBaseId));
    const model = useSelector((state: StoreState) => customizationBase && adaptSelectors.selectModelById(state, customizationBase.modelId));
    const activeTags = useSelector((state: StoreState) => state.adapt.activeTagFilters);
    const activeVersionTags = useSelector((state: StoreState) => state.adapt.activeVersionTags);
    const activeInputModalities = useSelector((state: StoreState) => adaptSelectors.selectActiveInputModalities(state));
    const activeOutputModalities = useSelector((state: StoreState) => adaptSelectors.selectActiveOutputModalities(state));

    const handleAddAeTitleRuleClick = useCallback(() => {
        const newAeTitleRule = createNewAeTitleRule(undefined, null, undefined, undefined,
            undefined, customizationBaseId, true);
        dispatch(aeTitleRuleAdded(newAeTitleRule));
    }, [dispatch, customizationBaseId]);

    const handleAddDicomRuleClick = useCallback(() => {
        const dicomRuleId = generateNewId();
        const newDicomAttribute = createNewDicomAttributeRule('', literalTextToRegex(''), dicomRuleId, true);
        const newDicomRule = createNewDicomRule([newDicomAttribute.id], dicomRuleId,
            customizationBaseId, null, undefined, true);
        dispatch(dicomRuleAdded(newDicomRule));
        dispatch(dicomAttributeRuleAdded(newDicomAttribute));
    }, [dispatch, customizationBaseId]);


    const getDicomAttributeRule = useCallback((id: string, runningNumber: number, onRemoveDicomRuleButtonClicked: () => void) => {
        return (<AdaptDicomAttributeRule key={id}
            dicomAttributeRuleId={id} runningNumber={runningNumber}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
            onRemoveDicomRule={onRemoveDicomRuleButtonClicked}
        />)
    }, [setUndoState, isUndoDisabled]);


    const getAeTitleRule = useCallback((id: string, runningNumber: number, isLastRule: boolean) => {
        return (<AdaptAeTitleRule key={id}
            aeTitleRuleId={id} runningNumber={runningNumber} isLastRule={isLastRule}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
        />);
    }, [setUndoState, isUndoDisabled]);

    
    const getDicomRule = useCallback((id: string, runningNumber: number, isLastRule: boolean) => {
        return (<AdaptDicomRule key={id}
            dicomRuleId={id} runningNumber={runningNumber} isLastRule={isLastRule}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
            getDicomAttributeRule={getDicomAttributeRule}
        />);
    }, [setUndoState, isUndoDisabled, getDicomAttributeRule]);


    if (customizationBase === undefined || model === undefined) {
        return null;
    }

    return (
        <SelectionRulesForm
            modelType={ModelType.Adapt}
            aeTitleRuleIds={customizationBase.aeTitleRules}
            dicomRuleIds={customizationBase.dicomRules}
            modelName={model.modelName}
            modelLabel={model.label}
            customizationName={customizationBase.customizationName}
            isCustomizationBaseModified={customizationBase.isModified}
            onAeTitleRuleAdded={handleAddAeTitleRuleClick}
            onDicomRuleAdded={handleAddDicomRuleClick}
            setUndoState={setUndoState}
            heading={heading}
            hideCustomizationName={hideCustomizationName}
            showMultipleOutputsWarning={showMultipleOutputsWarning}
            isUndoDisabled={isUndoDisabled}
            getAeTitleRule={getAeTitleRule}
            getDicomRule={getDicomRule}
            isDicomSelectionDisabled={false}
            activeTags={activeTags}
            activeVersionTags={activeVersionTags}
            activeInputModalities={activeInputModalities}
            activeOutputModalities={activeOutputModalities}
        />

    );
}

export default AdaptSelectionRules;
